import React, { useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '../../services/api';
import { FaTruck, FaTimes, FaCheckCircle } from 'react-icons/fa';
import { formatCurrency } from '../../utils/formatters';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 700px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const ClientSection = styled.div`
  margin-bottom: 1.5rem;
`;

const ClientGroup = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const ClientName = styled.h3`
  margin: 0 0 0.75rem 0;
  color: #2196F3;
  font-size: 1.1rem;
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 4px;
  border-left: 3px solid #2196F3;
`;

const OrderNumber = styled.span`
  font-weight: 600;
  color: #333;
`;

const OrderAmount = styled.span`
  color: #666;
  font-size: 0.95rem;
`;

const ClientTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  margin-top: 0.5rem;
  background: #e3f2fd;
  border-radius: 4px;
  font-weight: 600;
`;

const Summary = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;

  &:not(:last-child) {
    border-bottom: 1px solid #e0e0e0;
  }

  &:last-child {
    font-size: 1.1rem;
    font-weight: 700;
    color: #2196F3;
    padding-top: 1rem;
    margin-top: 0.5rem;
    border-top: 2px solid #2196F3;
  }
`;

const SummaryLabel = styled.span`
  color: #666;

  &.total {
    color: #2196F3;
  }
`;

const SummaryValue = styled.span`
  font-weight: 600;
  color: #333;

  &.total {
    color: #2196F3;
  }
`;

const NotesInput = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 80px;
  margin-bottom: 1.5rem;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2196F3;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: #f5f5f5;
  color: #666;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }
`;

const CreateButton = styled(Button)`
  background: #4CAF50;
  color: white;

  &:hover:not(:disabled) {
    background: #45a049;
  }
`;

const InfoMessage = styled.div`
  background: #e3f2fd;
  border-left: 4px solid #2196F3;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 4px;
  color: #1976D2;
  font-size: 0.95rem;
`;

const CreateDeliveryNoteModal = ({ orders, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');

  // Helper function to round to 5 cents (for TPV orders)
  const roundToFiveCents = (number) => {
    return Math.ceil(number * 20) / 20;
  };

  // Helper to determine if order was created with Calculadora Profesional
  const isCalculadoraPro = (order) => {
    if (!order) return false;
    // Only use fingerprint pattern - both TPV and CalculadoraPro can have measurementType
    const fp = order.fingerprint || '';
    return fp.startsWith('INT-') || fp.startsWith('EXT-');
  };

  // Helper to calculate final amount with correct rounding based on order type
  const calculateDisplayFinalAmount = (order) => {
    if (!order) return 0;
    const finalAmount = parseFloat(order.finalAmount || order.totalAmount || 0);

    // For TPV orders, apply 5-cent rounding to final amount WITH VAT
    // For CalculadoraPro orders, use finalAmount as-is (base already rounded)
    if (!isCalculadoraPro(order)) {
      // TPV order - round final amount to 5 cents
      return roundToFiveCents(finalAmount);
    }

    // CalculadoraPro order - use as-is
    return finalAmount;
  };

  // Group orders by client
  const ordersByClient = orders.reduce((acc, order) => {
    const clientId = order.client.id;
    if (!acc[clientId]) {
      acc[clientId] = {
        client: order.client,
        orders: [],
        total: 0
      };
    }
    acc[clientId].orders.push(order);
    acc[clientId].total += calculateDisplayFinalAmount(order);
    return acc;
  }, {});

  const clientGroups = Object.values(ordersByClient);
  const totalOrders = orders.length;
  const totalClients = clientGroups.length;
  const totalAmount = orders.reduce((sum, order) =>
    sum + calculateDisplayFinalAmount(order), 0
  );

  const createDeliveryNotesMutation = useMutation({
    mutationFn: async (orderIds) => {
      const response = await api.post('/delivery-notes/create-from-orders', {
        orderIds
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['deliveryNotes']);
      toast.success(data.message || `Se crearon ${data.deliveryNotes.length} albaranes correctamente`);
      onSuccess && onSuccess(data.deliveryNotes);
      onClose();
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.error || 'Error al crear los albaranes';
      const errorDetails = error.response?.data?.errors || error.response?.data?.details;
      console.error('Error creating delivery notes:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`${errorMsg}${errorDetails ? ': ' + JSON.stringify(errorDetails) : ''}`);
    }
  });

  const handleCreate = () => {
    const orderIds = orders.map(order => order.id);
    createDeliveryNotesMutation.mutate(orderIds);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FaTruck />
            Crear Albaranes
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </ModalHeader>

        <InfoMessage>
          Los pedidos se agruparán automáticamente por cliente. Se creará un albarán por cada cliente con sus pedidos correspondientes.
        </InfoMessage>

        <Summary>
          <SummaryRow>
            <SummaryLabel>Total de pedidos:</SummaryLabel>
            <SummaryValue>{totalOrders}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Clientes:</SummaryLabel>
            <SummaryValue>{totalClients}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Albaranes a crear:</SummaryLabel>
            <SummaryValue>{totalClients}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel className="total">Total:</SummaryLabel>
            <SummaryValue className="total">{formatCurrency(totalAmount)}</SummaryValue>
          </SummaryRow>
        </Summary>

        <ClientSection>
          {clientGroups.map((group, index) => (
            <ClientGroup key={index}>
              <ClientName>{group.client.name}</ClientName>
              <OrdersList>
                {group.orders.map(order => (
                  <OrderItem key={order.id}>
                    <OrderNumber>Pedido #{order.orderNumber}</OrderNumber>
                    <OrderAmount>
                      {formatCurrency(calculateDisplayFinalAmount(order))}
                    </OrderAmount>
                  </OrderItem>
                ))}
              </OrdersList>
              <ClientTotal>
                <span>Total para {group.client.name}:</span>
                <span>{formatCurrency(group.total)}</span>
              </ClientTotal>
            </ClientGroup>
          ))}
        </ClientSection>

        <ModalActions>
          <CancelButton onClick={onClose} disabled={createDeliveryNotesMutation.isLoading}>
            <FaTimes />
            Cancelar
          </CancelButton>
          <CreateButton
            onClick={handleCreate}
            disabled={createDeliveryNotesMutation.isLoading}
          >
            <FaCheckCircle />
            {createDeliveryNotesMutation.isLoading ? 'Creando...' : `Crear ${totalClients} Albarán(es)`}
          </CreateButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CreateDeliveryNoteModal;
